import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as promotionsService from "../service/promotionsService";
import type { PromotionsState, PromotionItemResult, PromotionConfirmOptions } from "../types";
import i18n from "@/i18n";

export const fetchPromotionBatches = createAsyncThunk(
  "promotions/fetchBatches",
  async ({ institutionId, fromAcademicYearId }: { institutionId: number; fromAcademicYearId?: number }) => {
    return await promotionsService.fetchPromotionBatches(institutionId, { fromAcademicYearId });
  }
);

export const createPromotionBatch = createAsyncThunk(
  "promotions/createBatch",
  async (
    {
      institutionId,
      fromAcademicGroupId,
      fromAcademicYearId,
      toAcademicYearId,
      examId,
    }: { institutionId: number; fromAcademicGroupId: number; fromAcademicYearId: number; toAcademicYearId: number; examId?: number }
  ) => {
    return await promotionsService.createPromotionBatch(institutionId, { fromAcademicGroupId, fromAcademicYearId, toAcademicYearId, examId });
  }
);

export const loadPromotionBatch = createAsyncThunk(
  "promotions/loadBatch",
  async ({ institutionId, batchId }: { institutionId: number; batchId: number }) => {
    return await promotionsService.fetchPromotionBatch(institutionId, batchId);
  }
);

export const updatePromotionItem = createAsyncThunk(
  "promotions/updateItem",
  async (
    {
      institutionId,
      batchId,
      itemId,
      result,
      toAcademicGroupId,
      examId,
    }: { institutionId: number; batchId: number; itemId: number; result: PromotionItemResult; toAcademicGroupId?: number; examId?: number }
  ) => {
    return await promotionsService.updatePromotionItem(institutionId, batchId, itemId, { result, toAcademicGroupId, examId });
  }
);

export const confirmPromotionBatch = createAsyncThunk(
  "promotions/confirmBatch",
  async ({ institutionId, batchId, ...options }: { institutionId: number; batchId: number } & PromotionConfirmOptions) => {
    return await promotionsService.confirmPromotionBatch(institutionId, batchId, options);
  }
);

export const revertPromotionBatch = createAsyncThunk(
  "promotions/revertBatch",
  async ({ institutionId, batchId, itemIds }: { institutionId: number; batchId: number; itemIds?: number[] }) => {
    return await promotionsService.revertPromotionBatch(institutionId, batchId, itemIds);
  }
);

const initialState: PromotionsState = {
  batches: [],
  activeBatch: null,
  activeItems: [],
  confirmErrors: [],
  confirmWarnings: [],
  loading: false,
  submitting: false,
  error: null,
};

const promotionsSlice = createSlice({
  name: "promotions",
  initialState,
  reducers: {
    clearActiveBatch(state) {
      state.activeBatch = null;
      state.activeItems = [];
      state.confirmErrors = [];
      state.confirmWarnings = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPromotionBatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromotionBatches.fulfilled, (state, action) => {
        state.loading = false;
        state.batches = action.payload;
      })
      .addCase(fetchPromotionBatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("promotions:promotionsSlicetsMsgs.failedToLoadPromotionBatches");
      })
      .addCase(createPromotionBatch.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createPromotionBatch.fulfilled, (state, action) => {
        state.submitting = false;
        state.activeBatch = action.payload.batch;
        state.activeItems = action.payload.items;
      })
      .addCase(createPromotionBatch.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("promotions:promotionsSlicetsMsgs.failedToDraftPromotionBatch");
      })
      .addCase(loadPromotionBatch.fulfilled, (state, action) => {
        state.activeBatch = action.payload.batch;
        state.activeItems = action.payload.items;
      })
      .addCase(updatePromotionItem.fulfilled, (state, action) => {
        state.activeBatch = action.payload.batch;
        state.activeItems = action.payload.items;
      })
      .addCase(updatePromotionItem.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("promotions:promotionsSlicetsMsgs.failedToUpdateStudentOutcome");
      })
      .addCase(confirmPromotionBatch.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(confirmPromotionBatch.fulfilled, (state, action) => {
        state.submitting = false;
        state.activeBatch = action.payload.batch;
        state.activeItems = action.payload.items;
        state.confirmErrors = action.payload.errors ?? [];
        state.confirmWarnings = action.payload.warnings ?? [];
      })
      .addCase(revertPromotionBatch.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(revertPromotionBatch.fulfilled, (state, action) => {
        state.submitting = false;
        state.activeBatch = action.payload.batch;
        state.activeItems = action.payload.items;
        state.confirmErrors = action.payload.errors ?? [];
        state.confirmWarnings = [];
      })
      .addCase(revertPromotionBatch.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("promotions:promotionsSlicetsMsgs.failedToRevertPromotions");
      })
      .addCase(confirmPromotionBatch.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("promotions:promotionsSlicetsMsgs.failedToConfirmPromotionBatch");
      });
  },
});

export const { clearActiveBatch } = promotionsSlice.actions;
export default promotionsSlice.reducer;
